import { Flex } from "@chakra-ui/react";
import { FormikProps } from "formik";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { useEffect, useState } from "react";
import { transformGuides } from "../../helpers/transformGuides";
import FileUploader from "../FileUploader/FileUploader";
import { BaseInput } from "../Controls/BaseInput";
import { BaseTextArea } from "../Controls/BaseTextArea";
import { SalesMaxOfferBlock } from "../Controls/SalesMaxOfferBlock";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import { Customer } from "@/model/customers";
import { ApplicationForm } from "../../model/applications";
import { BaseSelect, SelectOption } from "../Controls/BaseSelect";
import { clearCurrentProject, fetchAllProjects, fetchSingleProject } from "../../store/slices/projects";
import { CreatableProfileSelect } from "../Controls/CreatableProfileSelect";

interface CreateApplicationBlockProps {
  formik: FormikProps<ApplicationForm>;
}

export const CreateApplicationBlock = ({ formik }: CreateApplicationBlockProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);
  const dispatch = useAppDispatch();
  const { customers } = useAppSelector((state: RootState) => state.customers);

  const { projects } = useAppSelector((state: RootState) => state.projects);
  const { currentProject } = useAppSelector((state: RootState) => state.projects);
  const [jobFormatOptions, setJobFormatOptions] = useState<SelectOption[]>([]);
  const [jobWorkloadOptions, setJobWorkloadOptions] = useState<SelectOption[]>([]);
  const [attractionPeriodOptions, setAttractionPeriodOptions] = useState<SelectOption[]>([]);
  const [profileOptions, setProfileOptions] = useState<SelectOption[]>([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<SelectOption[]>([]);
  const [gradeOptions, setGradeOptions] = useState<SelectOption[]>([]);

  const [projectsOptions, setProjectsOptions] = useState<SelectOption[]>([]);

  const customerOptions: SelectOption[] = customers.items.map((el: Customer) => {
    const value = el.id !== undefined && typeof el.id === "number" ? el.id : undefined;
    const label = el.name !== undefined && typeof el.name === "string" ? el.name : undefined;

    return {
      value: value,
      label: label || "",
    };
  });

  useEffect(() => {
    if (guides) {
      const resultJobFormatList = transformGuides(guides.JobFormat).map((o: any) => ({ value: String(o.value), label: String(o.label || "") }));
      setJobFormatOptions(resultJobFormatList);
      const resultJobWorkloadList = transformGuides(guides.JobWorkload).map((o: any) => ({ value: String(o.value), label: String(o.label || "") }));
      setJobWorkloadOptions(resultJobWorkloadList);
      const resultAttractionPeriodList = transformGuides(guides.AttractionPeriod).map((o: any) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setAttractionPeriodOptions(resultAttractionPeriodList);
      const resultProfileList = transformGuides(guides.ApplicationProfile).map((o: any) => ({ value: o.value, label: String(o.label || "") }));
      setProfileOptions(resultProfileList);
      const resultLegalEntities = transformGuides(guides.LegalEntity).map((o: any) => ({ value: Number(o.value), label: String(o.label || "") }));
      setLegalEntityOptions(resultLegalEntities);
      const grades = (transformGuides(guides.Grade) as any[]).map((o) => ({
        value: String(o.value),
        label: String(o.label || ""),
      })) as unknown as SelectOption[];
      setGradeOptions(grades);
    }
  }, [guides]);

  useEffect(() => {
    const useCustomerSpecific = formik.values.customer_id !== null && formik.values.customer_id !== undefined;
    const items = useCustomerSpecific ? projects?.items || [] : projects?.items || [];
    const options: SelectOption[] = items.map((el: any) => ({
      value: el?.id !== undefined && typeof el?.id === "number" ? el.id : undefined,
      label: el?.name !== undefined && typeof el?.name === "string" ? el.name : "",
    }));
    setProjectsOptions(options);
  }, [projects, formik.values.customer_id]);

  useEffect(() => {
    if (formik.values.customer_id !== null && formik.values.customer_id !== undefined) {
      dispatch(fetchAllProjects({ params: { filters: { customer_id: formik.values.customer_id.toString() } } }));
    }
  }, [dispatch, formik.values.customer_id]);

  useEffect(() => {
    if (currentProject) {
      formik.setFieldValue("customer_id", currentProject.customer?.id ?? null);
      formik.setFieldValue("project_description", currentProject.description ?? "");
      formik.setFieldValue("legal_entity_id", currentProject.legal_entity?.id ?? null);
      formik.setFieldValue("bitrix_contract_link", (currentProject as any).bitrix_contract_link ?? "");
    } else {
      if (formik.values.project_id === null) {
        formik.setFieldValue("project_description", "");
        formik.setFieldValue("legal_entity_id", null);
        formik.setFieldValue("bitrix_contract_link", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  return (
    <FormWrapper>
      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle={"Название должности"}
          requiredField={true}
          value={formik.values.name}
          onChange={formik.handleChange}
          name="name"
          placeholder="Введите название должности"
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Заказчик"
          requiredField
          options={customerOptions}
          placeholder="Выберите заказчика"
          value={formik.values.customer_id}
          onChange={(value) => {
            const nextCustomerId = value ? Number(value) : null;
            dispatch(clearCurrentProject());
            formik.setFieldValue("customer_id", nextCustomerId);
            formik.setFieldValue("project_id", null);
            formik.setFieldValue("project_description", "");
            formik.setFieldValue("legal_entity_id", null);
            formik.setFieldValue("bitrix_contract_link", "");
            formik.setFieldValue("job_format", "");
          }}
          containerStyles={{ width: "100%" }}
          isEmpty={formik.values.customer_id === null}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Проект"
          requiredField
          options={projectsOptions}
          placeholder="Выберите проект"
          key={`project-select-${formik.values.customer_id ?? "none"}`}
          value={formik.values.project_id}
          onChange={(value) => {
            const id = value ? Number(value) : null;
            if (id) {
              dispatch(fetchSingleProject({ id }));
            }
            formik.setFieldValue("project_id", id);
            if (id === null) {
              formik.setFieldValue("project_description", "");
              formik.setFieldValue("legal_entity_id", null);
              formik.setFieldValue("bitrix_contract_link", "");
            }
          }}
          containerStyles={{ width: "100%" }}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Юр. лицо"
          options={legalEntityOptions}
          requiredField={true}
          placeholder="Выберите юр. лицо"
          value={formik.values.legal_entity_id}
          onChange={(value) => formik.setFieldValue("legal_entity_id", value ? Number(value) : null)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
        />
        <CreatableProfileSelect
          fieldTitle="Профиль"
          requiredField
          options={profileOptions}
          placeholder="Выберите профиль или начните вводить для поиска"
          value={formik.values.profile_id}
          onChange={(value) => formik.setFieldValue("profile_id", value ? Number(value) : null)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isEmpty={!formik.values.profile_id}
        />
        <BaseSelect
          fieldTitle="Грейд"
          requiredField
          options={gradeOptions}
          placeholder="Выберите грейд"
          value={formik.values.candidate_grade}
          onChange={(value) => formik.setFieldValue("candidate_grade", Array.isArray(value) ? value : [])}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isMulti
          isEmpty={!formik.values.candidate_grade || formik.values.candidate_grade.length === 0}
        />
      </Flex>

      <Flex gap="20px" paddingBottom="20px" flexDirection="column">
        <BaseTextArea
          fieldTitle={"Требования к кандидату"}
          requiredField={true}
          name="candidate_requirements"
          value={formik.values.candidate_requirements}
          onChange={formik.handleChange}
          placeholder="Опишите требования к кандидату"
        />

        <BaseTextArea
          fieldTitle={"Обязанности кандидата"}
          name="job_duties"
          requiredField={true}
          value={formik.values.job_duties}
          onChange={formik.handleChange}
          placeholder="Опишите обязанности кандидата"
        />

        <BaseTextArea
          fieldTitle="Описание проекта"
          requiredField
          name={"project_description"}
          onChange={formik.handleChange}
          value={formik.values.project_description}
          placeholder="Краткое описание проекта"
        />

        <BaseInput
          fieldTitle={"Ссылка на договор (Bitrix 24)"}
          value={(formik.values as any).bitrix_contract_link || ""}
          onChange={formik.handleChange}
          name="bitrix_contract_link"
          placeholder="Вставьте ссылку на договор"
        />

        <SalesMaxOfferBlock
          valueGeneral={formik.values.sales_max_offer_general || ""}
          value1={formik.values.sales_max_offer_moscow || ""}
          value2={formik.values.sales_max_offer_regions || ""}
          idGeneral="sales_max_offer_general"
          id1="sales_max_offer_moscow"
          id2="sales_max_offer_regions"
          onChangeGeneral={(value) => formik.setFieldValue("sales_max_offer_general", value)}
          onChange1={(value) => formik.setFieldValue("sales_max_offer_moscow", value)}
          onChange2={(value) => formik.setFieldValue("sales_max_offer_regions", value)}
        />
      </Flex>

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Маржинальность"
          requiredField
          onChange={formik.handleChange}
          value={`${formik.values.marginality ?? ""}`}
          name={"marginality"}
          placeholder="Введите маржинальность"
          containerStyles={{ flex: 1 }}
        />
        <BaseInput
          fieldTitle="Ставка закупки (max)"
          onChange={formik.handleChange}
          value={(formik.values as any).purchase_rate_max || ""}
          name={"purchase_rate_max"}
          placeholder="Введите максимальную ставку закупки"
          containerStyles={{ flex: 1 }}
        />
        <BaseInput
          fieldTitle="Сколько человек нужно нанять"
          value={`${formik.values.required_number ?? ""}`}
          onChange={formik.handleChange}
          name="required_number"
          requiredField={true}
          placeholder="Введите количество человек"
          containerStyles={{ flex: 1 }}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Локация поиска"
          value={formik.values.location}
          onChange={formik.handleChange}
          name="location"
          placeholder="Город/регион"
          containerStyles={{ flex: 1 }}
        />
        <BaseSelect
          fieldTitle="Формат работы"
          requiredField
          options={jobFormatOptions}
          placeholder="Выберите формат"
          value={formik.values.job_format}
          onChange={(value) => formik.setFieldValue("job_format", value)}
          containerStyles={{ flex: 1 }}
        />
      </Flex>

      <BaseTextArea
        fieldTitle="Адрес офиса"
        name={"office_address"}
        onChange={formik.handleChange}
        value={formik.values.office_address}
        placeholder="Укажите адрес офиса (если есть)"
      />

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Занятость"
          requiredField
          options={jobWorkloadOptions}
          placeholder="Выберите занятость"
          value={formik.values.job_workload}
          onChange={(value) => formik.setFieldValue("job_workload", value)}
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Срок привлечения"
          requiredField
          options={attractionPeriodOptions}
          placeholder="Выберите срок"
          value={formik.values.attraction_period}
          onChange={(value) => formik.setFieldValue("attraction_period", value)}
          containerStyles={{ width: "100%" }}
        />
      </Flex>

      <BaseTextArea
        fieldTitle={"Комментарии к заявке"}
        id="comments"
        value={formik.values.comments}
        onChange={formik.handleChange}
        placeholder="Добавьте комментарий"
      />

      <Flex className={"form-column"} flex="1" paddingTop="20px">
        <FileUploader onChange={(files) => formik.setFieldValue("attachments", files)} />
      </Flex>
    </FormWrapper>
  );
};
